import React, { useEffect, useState } from 'react'
import { Card } from 'react-bootstrap'
import Alert from 'react-bootstrap/Alert'
import Helmet from 'react-helmet'
import { Link, withRouter } from 'react-router-dom'

import Moment from 'moment'

import AppointmentDashboardCard from '../components/AppointmentDashboardCard'
import DashboardWrapper from '../components/DashboardWrapper'
import FAIcon from '../components/FAIcon'
import ReportDashboardCard from '../components/ReportDashboardCard'
import TaskCard from '../components/TaskCard'
import createFormHelpers from '../FormHelpers'
import AppointmentsService from '../services/AppointmentsService'
import CasesService from '../services/CasesService'
import GeneralTasksService from '../services/GeneralTasksService'
import TasksService from '../services/TasksService'
import Permissions, { CAPABILITIES } from '../utils/Permissions'

import './Home.scss'

const reportModelFields = [
  'caseNumber',
  'reportNumber',
  'case.client.firstName',
  'case.client.lastName',
  'submittedOnUtc',
  'fromDateTimeUtc',
  'toDateTimeUtc',
  'totalBillableTime',
  'reportType',
  'sequenceNumber',
]

export default withRouter(({ user, settings, history }) => {
  const [cases, setCases] = useState([{}])
  const [tasks, setTasks] = useState([])
  const [reports, setReports] = useState([])
  const [appointments, setAppointments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  const { getValue } = createFormHelpers({})

  useEffect(() => {
    const promises = []

    const statusToFetch = user.roles.includes('invoicer')
      ? settings.caseStatuses.approved.key
      : settings.caseStatuses.submitted.key

    const reportsPromise = CasesService.getReportsByStatus(statusToFetch, reportModelFields).then(r => setReports(r))
    promises.push(reportsPromise)

    if (Permissions.can(user, CAPABILITIES.VIEW_DASHBOARD_APPOINTMENTS)) {
      const casePromise = CasesService.getAll(['caseNumber', 'client.firstName', 'client.lastName']).then(caseObjs => {
        setCases(caseObjs)
        return caseObjs
      })
      promises.push(casePromise)

      const tasksPromise = Promise.all([TasksService.getAll(true), GeneralTasksService.getMyTasks()]).then(
        ([caseTasks, generalTasks]) => {
          const incompleteGeneralTasks = generalTasks.filter(t => !t.complete)
          setTasks([...caseTasks, ...incompleteGeneralTasks])
        },
      )
      promises.push(tasksPromise)

      const appointmentsPromise = AppointmentsService.getAll(true).then(r => {
        setAppointments(r)
      })
      promises.push(appointmentsPromise)
    }

    Promise.all(promises)
      .then(() => {
        setIsLoading(false)
      })
      .catch(err => {
        setError(err)
        setIsLoading(false)
      })

    // eslint-disable-next-line
  }, [])

  const handleButtonClick = (path, extraState = {}) => {
    const [pathname, search] = String(path || '').split('?')
    history.push({
      pathname,
      search: search ? `?${search}` : '',
      state: { from: history.location.pathname, ...extraState },
    })
  }

  const getFormattedDateTimeValue = (startLocal, endLocal, appointment) => {
    if (!startLocal && !appointment?.startUtc) {
      return ''
    }
    if (appointment?.isAllDay && appointment?.startUtc) {
      const date = Moment.utc(appointment.startUtc).format('dddd, MMMM DD, YYYY')
      return <p className="text-details mb-0">{date}</p>
    }
    if (!!startLocal && !!endLocal) {
      const date = Moment(startLocal).format('dddd, MMMM DD, YYYY')
      const startTime = Moment(startLocal).format('h:mma')
      const endTime = Moment(endLocal).format('h:mma')

      return (
        <p className="text-details mb-0">
          {date} &#8226; {startTime}-{endTime}
        </p>
      )
    } else if (startLocal) {
      const startM = Moment(startLocal)
      const date = startM.format('dddd, MMMM DD, YYYY')
      const startTime = startM.format('h:mma')
      return (
        <p className="text-details mb-0">
          {date} &#8226; {startTime}
        </p>
      )
    } else {
      return ''
    }
  }

  const convertUtcToLocalDateTime = (utcDateTime, appointment) => {
    if (!utcDateTime) return null
    if (appointment?.isAllDay) {
      return Moment(Moment.utc(utcDateTime).format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day').format()
    }
    return Moment.utc(utcDateTime).local().format()
  }

  const getAppointmentsList = () => {
    const appointmentsList = []

    if (appointments) {
      // sort the list of appointments by their start date
      const sortedAppointmentsList = appointments
        .slice()
        .sort((a, b) => (a.startUtc > b.startUtc ? 1 : a.startUtc === b.startUtc ? 1 : -1))

      // add the appointments to the list until we have 6 or we run out of appointments
      let i = 0
      while (appointmentsList.length < 6 && i < sortedAppointmentsList.length) {
        appointmentsList.push(sortedAppointmentsList[i])
        i++
      }
    }

    return appointmentsList
  }

  const appointmentsList = getAppointmentsList()

  let showReports = true
  if (reports.length === 0) {
    if (!Permissions.can(user, CAPABILITIES.VIEW_DASHBOARD_REPORTS)) {
      showReports = false
    }
  }

  return (
    <DashboardWrapper user={user}>
      {!isLoading ? (
        <React.Fragment>
          <Helmet title="Home" />
          <div className="dashboard-container">
            {showReports && <CaseReportsSection caseReports={reports} settings={settings} />}

            {!!user && !!user.roles && Permissions.can(user, CAPABILITIES.VIEW_DASHBOARD_APPOINTMENTS) && (
              <div className="row">
                <div className="dashboard-section appointments-dashboard-container mt-3 mb-4 col-lg-8 col-md-7 col-sm-12">
                  <div className="col-md-12 px-0">
                    <SectionHeader
                      icon="calendar-day"
                      title={`Appointments ${
                        appointmentsList.length !== appointments.length
                          ? `(${appointmentsList.length} of ${appointments.length})`
                          : ''
                      }`}
                      className={!showReports && 'text-white'}
                    />
                    <div className="row">
                      {!!appointmentsList && appointmentsList.length !== 0 ? (
                        appointmentsList.map((appointment, index) => {
                          return (
                            <AppointmentDashboardCard
                              key={`appointment-card-${index}`}
                              appointment={appointment}
                              getValue={getValue}
                              getFormattedDateTimeValue={getFormattedDateTimeValue}
                              convertUtcToLocalDateTime={convertUtcToLocalDateTime}
                              handleButtonClick={handleButtonClick}
                              cases={cases}
                              showCaseDetails={true}
                            />
                          )
                        })
                      ) : (
                        <div className="col-md-12">
                          <Card className="col-12 mb-3 shadow">
                            <Card.Body>
                              <p className="text-center">
                                <strong>No upcoming or recent appointments to complete</strong>
                              </p>
                              <button
                                type="button"
                                className="btn btn-secondary btn-block"
                                onClick={() => handleButtonClick('/appointments')}
                              >
                                Add an Appointment
                              </button>
                            </Card.Body>
                          </Card>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="dashboard-section tasks-dashboard-container mt-3 mb-4 col-lg-4 col-md-5 col-sm-12">
                  <SectionHeader
                    icon="calendar-check"
                    title="Tasks"
                    className={!showReports && 'text-white-md-up'}
                    action={
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => handleButtonClick('/tasks/new')}
                        title="Add General Task"
                      >
                        <FAIcon name="plus" />
                      </button>
                    }
                  />
                  <TaskCard
                    incompleteTasks={tasks}
                    handleButtonClick={handleButtonClick}
                    getValue={getValue}
                    cases={cases}
                    isDashboardView={true}
                    user={user}
                    onTaskDeleted={taskNumber => setTasks(prev => prev.filter(t => t.taskNumber !== taskNumber))}
                  />
                  <div className="text-right mt-1">
                    <Link to="/tasks" className="small text-muted">
                      All Tasks &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            )}
            {error && (
              <Alert variant="danger">
                <strong>Error:</strong> {error.message}
              </Alert>
            )}
          </div>
        </React.Fragment>
      ) : (
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
    </DashboardWrapper>
  )
})

function SectionHeader({ title, icon, className, action }) {
  return (
    <div className={`d-flex justify-content-between align-items-center mb-2`}>
      <h2 className={`h5 ml-1 section-header mb-0 ${className ?? ''}`}>
        <FAIcon name={icon} className={'mr-3'} />
        {title}
      </h2>
      {action}
    </div>
  )
}

function CaseReportsSection({ caseReports, settings }) {
  return (
    <div className="dashboard-section">
      <SectionHeader icon="file-invoice" title="Progress Reports" className="text-white" />
      {!caseReports || caseReports.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div className="text-center">No Reports to review</div>
          </div>
        </div>
      ) : (
        <div className="row">
          {caseReports.map((caseReport, i) => (
            <div key={i} className="col-md-4 mb-3">
              <ReportDashboardCard caseReport={caseReport} settings={settings} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
